import dayjs from 'dayjs';
import type { TFunction } from 'i18next';

export type PresetParams = Record<string, string[]>;

export type PresetView = {
  id: string;
  label: string;
  description: string;
  // Resolved on use, not at module load: Past due is relative to today, so a
  // long-lived tab must not keep filtering against the day it was opened.
  params: () => PresetParams;
};

export const EXTERNAL_PARAMS: PresetParams = { grantOrigin: ['ext'] };

export const UNTRUSTED_EXTERNAL_PARAMS: PresetParams = {
  grantOrigin: ['ext'],
  trustStatus: ['untrusted'],
};

// Excludes 'federated', whose principal is an identity provider, not an account.
export const CROSS_ACCOUNT_PARAMS: PresetParams = {
  grantScope: ['same-org', 'cross-org', 'public'],
};

const PRESET_PARAMS: Pick<PresetView, 'id' | 'params'>[] = [
  {
    id: 'all',
    params: () => ({}),
  },
  {
    id: 'external',
    params: () => EXTERNAL_PARAMS,
  },
  {
    id: 'untrusted-external',
    params: () => UNTRUSTED_EXTERNAL_PARAMS,
  },
  {
    id: 'cross-org',
    params: () => ({ grantScope: ['cross-org'] }),
  },
  {
    id: 'public',
    params: () => ({ grantScope: ['public'] }),
  },
  {
    id: 'aws-service',
    params: () => ({ principalType: ['aws_service'] }),
  },
  {
    id: 'past-due',
    // Yesterday, not today: the bound is inclusive, and a review due today is
    // not yet past due — matches isReviewOverdue, which reddens the date in the
    // table. Sets the same param as the sidebar's Next review "to" field, so
    // the chip fills that field in the way the other chips fill theirs.
    params: () => ({
      nextReviewAtEnd: [dayjs().subtract(1, 'day').format('YYYY-MM-DD')],
    }),
  },
];

/** What each preset is called and what it filters to. */
const presetText = (
  t: TFunction<'accessGrants'>,
): Record<string, Pick<PresetView, 'label' | 'description'>> => ({
  all: {
    label: t('all'),
    description: t('allGrantsInternalAndExternal'),
  },
  external: {
    label: t('external'),
    description: t('grantsFromPrincipalsOutsideThisAwsAccount'),
  },
  'untrusted-external': {
    label: t('untrustedExternal'),
    description: t('externalGrantsNotOnYourTrustedPrincipals'),
  },
  'cross-org': {
    label: t('crossOrg'),
    description: t('grantsFromASpecificAwsAccountThat'),
  },
  public: {
    label: t('public'),
    description: t('grantsOpenToEveryoneThePrincipalIs'),
  },
  'aws-service': {
    label: t('awsService'),
    description: t('grantsToAwsServicesEGLambda'),
  },
  'past-due': {
    label: t('pastDue'),
    description: t('grantsWhoseNextReviewDateHasPassed'),
  },
});

export const presetViews = (t: TFunction<'accessGrants'>): PresetView[] =>
  PRESET_PARAMS.map((preset) => ({ ...preset, ...presetText(t)[preset.id] }));

const KPI_TILE_PARAMS = [
  EXTERNAL_PARAMS,
  UNTRUSTED_EXTERNAL_PARAMS,
  CROSS_ACCOUNT_PARAMS,
];

export const PRESET_KEYS = Array.from(
  new Set(
    [...PRESET_PARAMS.map((view) => view.params()), ...KPI_TILE_PARAMS].flatMap(
      (params) => Object.keys(params),
    ),
  ),
);

export function applyPresetParams(
  current: URLSearchParams,
  params: PresetParams,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of PRESET_KEYS) {
    next.delete(key);
  }
  for (const [key, values] of Object.entries(params)) {
    for (const value of values) {
      next.append(key, value);
    }
  }
  return next;
}
